import { FC, useState } from "react";
import { MiBox, MiList, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_LABELS,
  CONFIG_PAGES,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { ITenantHolidayResponse } from "@miplatform/types";
import { AddButton } from "@mibuildweb/components";
import {
  usePermissions,
  useTenantHoliday,
  useTenantHolidays,
} from "@mibuildweb/hooks";
import { ConfigHolidayListCard } from "./components/ConfigHolidayListCard";
import { CONFIG_PATHS } from "../../../routes/config/constants";
import { ConfigPageLayout } from "../ConfigPageLayout";

export const ConfigHolidayList: FC = () => {
  const { navigate } = useRouter();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] =
    useState<ITenantHolidayResponse | null>(null);

  const { tenantHolidays, loading, getTenantHolidays, setTenantHolidays } =
    useTenantHolidays();

  const {
    loading: isHolidayLoading,
    deleteTenantHoliday,
    refresh,
  } = useTenantHoliday();

  const { hasPermission } = usePermissions();

  const canDeleteHoliday = hasPermission(PERMISSIONS.TENANT_HOLIDAY.DELETE);
  const canCreateHoliday = hasPermission(PERMISSIONS.TENANT_HOLIDAY.CREATE);
  const canUpdateHoliday = hasPermission(PERMISSIONS.TENANT_HOLIDAY.UPDATE);

  const handleOpenDeleteModal = (holiday: ITenantHolidayResponse) => {
    setSelectedHoliday(holiday);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (selectedHoliday && canDeleteHoliday) {
      deleteTenantHoliday(selectedHoliday);
      setSelectedHoliday(null);
      setOpenDeleteModal(false);
    }
  };

  const handleAddHoliday = () => {
    navigate(`${CONFIG_PATHS[CONFIG_PAGES.HOLIDAYS]}/add-holiday`);
  };

  const handleEditHoliday = (holiday: ITenantHolidayResponse) => {
    navigate(`${CONFIG_PATHS[CONFIG_PAGES.HOLIDAYS]}/${holiday.id}`);
  };

  return (
    <ConfigPageLayout
      title="Holiday Schedule"
      titleActionComponent={
        canCreateHoliday ? (
          <AddButton label="Add Holiday" onClick={handleAddHoliday} />
        ) : undefined
      }
      hideOnBack
      loading={loading || isHolidayLoading}
    >
      <MiBox>
        <MiList
          viewType="card"
          data={tenantHolidays}
          dataProvider={getTenantHolidays}
          setData={setTenantHolidays}
          CardComponent={ConfigHolidayListCard}
          cardProps={{
            onEdit: canUpdateHoliday ? handleEditHoliday : () => {},
            onDelete: canDeleteHoliday ? handleOpenDeleteModal : undefined,
          }}
          paginationMode={PAGINATION.SERVER}
          refresh={refresh}
          entityName="Holidays"
          loading={loading || isHolidayLoading}
          groupedMode
          groupedCriteria="year"
          emptyListProps={{
            text: "No Holidays found",
          }}
        />
      </MiBox>
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal}
        textPositive={BUTTON_LABELS.DELETE}
        handleNegative={() => setOpenDeleteModal(false)}
        handlePositive={handleConfirmDelete}
        description={`You are about to delete ${selectedHoliday?.name}. Are you sure you would like to continue?`}
        buttonPositiveColor="error"
      />
    </ConfigPageLayout>
  );
};
